import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { Radio } from "@/components/Radio/Radio";
import { Spinner } from "@/components/Spinner/Spinner";

import { useGetCopywritingFrameworksQuery } from "@/pages/Private/redux/copywritingFramework/copywritingFramework.api";
import {
	CopywritingFramework,
	MessageType,
} from "@/pages/Private/pages/Company/schema/copywritingFramework";
import { useGetFrameworkEffectivenessSummaryQuery } from "@/pages/Private/redux/frameworkEffectiveness/frameworkEffectiveness.api";
import { FrameworkEffectivenessItem } from "@/pages/Private/redux/frameworkEffectiveness/frameworkEffectiveness.schema";
import { formatFrameworkName } from "@/utils/formatFrameworkName";

import { FrameworkOverrides } from "../types";

interface ChooseFrameworksModalProps {
	isOpened: boolean;
	onClose: () => void;
	onSave: (overrides: FrameworkOverrides) => void;
	initialOverrides?: FrameworkOverrides;
	personaCategory?: string | null;
	companyCategory?: string | null;
}

interface StepConfig {
	key: keyof FrameworkOverrides;
	labelKey: string;
	filter: (fw: CopywritingFramework) => boolean;
}

const STEPS: StepConfig[] = [
	{
		key: "step1FrameworkId",
		labelKey: "step1",
		filter: (fw) => fw.enabled && fw.applicableTo.includes(MessageType.INTRO),
	},
	{
		key: "step2FrameworkId",
		labelKey: "step2",
		filter: (fw) => fw.enabled && fw.applicableTo.includes(MessageType.FOLLOW_UP),
	},
	{
		key: "step3FrameworkId",
		labelKey: "step3",
		filter: (fw) => fw.enabled && fw.applicableTo.includes(MessageType.FOLLOW_UP),
	},
];

export const ChooseFrameworksModal: FunctionComponent<ChooseFrameworksModalProps> = ({
	isOpened,
	onClose,
	onSave,
	initialOverrides,
	personaCategory,
	companyCategory,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`messages.generateBar.frameworks.${key}`), [t]);

	const { data: frameworksData, isLoading } = useGetCopywritingFrameworksQuery();

	const effectivenessParams = useMemo(
		() => ({
			...(personaCategory ? { personaCategory } : {}),
			...(companyCategory ? { companyCategory } : {}),
		}),
		[personaCategory, companyCategory]
	);
	const { data: effectivenessData } = useGetFrameworkEffectivenessSummaryQuery(
		effectivenessParams,
		{ skip: !isOpened }
	);

	const effectivenessByName = useMemo(() => {
		const map = new Map<string, FrameworkEffectivenessItem>();

		effectivenessData?.items?.forEach((item) => {
			map.set(item.frameworkName.toLowerCase(), item);
		});

		return map;
	}, [effectivenessData]);

	const [selections, setSelections] = useState<FrameworkOverrides>(() => ({
		step1FrameworkId: initialOverrides?.step1FrameworkId ?? null,
		step2FrameworkId: initialOverrides?.step2FrameworkId ?? null,
		step3FrameworkId: initialOverrides?.step3FrameworkId ?? null,
	}));

	useEffect(() => {
		setSelections({
			step1FrameworkId: initialOverrides?.step1FrameworkId ?? null,
			step2FrameworkId: initialOverrides?.step2FrameworkId ?? null,
			step3FrameworkId: initialOverrides?.step3FrameworkId ?? null,
		});
	}, [initialOverrides, isOpened]);

	const handleSelect = useCallback(
		(stepKey: keyof FrameworkOverrides, frameworkId: number | null) => {
			setSelections((prev: FrameworkOverrides) => ({ ...prev, [stepKey]: frameworkId }));
		},
		[]
	);

	const handleSave = useCallback(() => {
		onSave(selections);
	}, [onSave, selections]);

	const frameworkLists = useMemo(() => {
		const all = frameworksData ?? [];

		return STEPS.map((step) => all.filter(step.filter));
	}, [frameworksData]);

	return (
		<Modal
			cancelButtonText={ts("cancel")}
			handleClose={onClose}
			handleSave={handleSave}
			isOpened={isOpened}
			size="lg"
			submitButtonText={ts("save")}
			title={ts("title")}
			// The GenerateBar floating panel is portaled at z-[9998]
			// (useAddInboxChat) — the modal must stack above it.
			zIndex={10000}
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<Spinner color="#4D66F5" />
				</div>
			) : (
				<div className="flex flex-col gap-6">
					{STEPS.map((step, idx) => (
						<div className="flex flex-col gap-3" key={String(step.key)}>
							<h3 className="text-sm font-semibold text-text uppercase tracking-wider">
								{ts(step.labelKey)}
							</h3>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
								<FrameworkCard
									isSelected={selections[step.key] === null}
									name={ts("autoSuggest")}
									description={ts("autoSuggestDescription")}
									onSelect={() => handleSelect(step.key, null)}
								/>
								{frameworkLists[idx].map((fw) => (
									<FrameworkCard
										key={fw.id}
										effectiveness={effectivenessByName.get(fw.name.toLowerCase())}
										isSelected={selections[step.key] === fw.id}
										name={formatFrameworkName(fw.name)}
										description={fw.description}
										onSelect={() => handleSelect(step.key, fw.id)}
									/>
								))}
							</div>
						</div>
					))}
				</div>
			)}
		</Modal>
	);
};

interface FrameworkCardProps {
	isSelected: boolean;
	name: string;
	description: string;
	onSelect: () => void;
	effectiveness?: FrameworkEffectivenessItem;
}

const FrameworkCard: FunctionComponent<FrameworkCardProps> = ({
	isSelected,
	name,
	description,
	onSelect,
	effectiveness,
}) => (
	<div
		className={`rounded-xl border p-3 cursor-pointer transition-colors ${
			isSelected
				? "border-brand-light bg-blue-50"
				: "border-border-light bg-brand-white hover:bg-gray-50"
		}`}
		data-testid="framework-card"
		onClick={onSelect}
	>
		<div className="flex items-start gap-2">
			<div className="mt-0.5 shrink-0">
				<Radio isChecked={isSelected} />
			</div>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2">
					<h4 className="text-sm font-medium text-text truncate">{name}</h4>
					{effectiveness && (
						<span
							className="shrink-0 rounded-md border border-border-light px-1.5 py-0.5 text-[10px] font-semibold text-text-meta"
							data-testid="framework-effectiveness-badge"
							title={`${effectiveness.totalUses} sends · ${effectiveness.positiveSentimentRatePct}% positive · ${effectiveness.meetingBookedRatePct}% meetings`}
						>
							{effectiveness.replyRatePct}% reply
						</span>
					)}
				</div>
				<p className="text-xs text-text-meta line-clamp-2 mt-0.5">{description}</p>
			</div>
		</div>
	</div>
);
